import { css } from '@emotion/react';
import type { Placement } from '@floating-ui/react';
import { V1 as AiSettingsV1 } from '@frontend/api-ai-settings';
import { useTranslation } from '@frontend/i18n';
import { Icon, IconName } from '@frontend/icons';
import { useMatchMedia } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import {
  PopoverMenu,
  PopoverMenuItem,
  Text,
  Truncated,
  usePopoverMenu,
  NakedButton,
  useTooltip,
} from '@frontend/design-system';

/** Converts an ISO 8601 duration (e.g. "PT1H30M", "PT45M") to total minutes. */
export const iso8601ToMinutes = (iso: string): number => {
  const hours = parseInt(iso.match(/(\d+)H/)?.[1] ?? '0', 10);
  const minutes = parseInt(iso.match(/(\d+)M/)?.[1] ?? '0', 10);
  return hours * 60 + minutes;
};

type Props = {
  locationId: string;
  orgId: string;
  selectedId?: string;
  onSelect: (appointmentTypeId: string) => void;
  placement?: Placement;
  leadingIcon?: IconName;
  useNameLabel?: boolean;
};

// TrueLark proxy page service selector drop down
export const ServiceSelector = ({
  locationId,
  orgId,
  selectedId,
  onSelect,
  placement = 'bottom-start',
  leadingIcon = undefined,
  useNameLabel = true,
}: Props) => {
  const { t } = useTranslation('schedule');
  const isMedium = useMatchMedia({ maxWidth: 1160 });

  const { data } = AiSettingsV1.Queries.useGetSchedulingAppointmentTypesQuery({
    request: { locationId, orgId },
    options: { enabled: !!locationId && !!orgId },
  });

  const appointmentTypes = data?.appointmentTypes ?? [];
  const resolvedId = selectedId ?? appointmentTypes[0]?.id;
  const selectedLabelName = appointmentTypes.find((a) => a.id === resolvedId)?.name ?? appointmentTypes[0]?.name;
  const selectedLabelDuration =
    appointmentTypes.find((a) => a.id === resolvedId)?.duration ?? appointmentTypes[0]?.duration;

  const { getTriggerProps, getMenuProps, getItemProps } = usePopoverMenu({ placement });
  const isOpen = getMenuProps().isOpen;

  const { Tooltip, triggerProps: tooltipTriggerProps, tooltipProps } = useTooltip({ trigger: 'hover' });

  return (
    <>
      <NakedButton
        css={[
          appointmentTypeSelectorStyles.trigger,
          { padding: isMedium ? theme.spacing(1, 1.5) : theme.spacing(0.5, 1.5) },
        ]}
        {...getTriggerProps()}
      >
        {leadingIcon && (
          <span {...(!useNameLabel && isMedium ? tooltipTriggerProps : {})}>
            <Icon name={leadingIcon} size={16} color='light' />
          </span>
        )}
        {useNameLabel ? (
          <>
            <Truncated as={Text} size='medium' weight='bold' color='default' css={{ maxWidth: 120 }}>
              {selectedLabelName}
            </Truncated>
            {selectedLabelDuration && (
              <Text as='span' size='small' weight='light' color='subdued' css={{ marginLeft: theme.spacing(1) }}>
                {t('{{count}} min', { count: iso8601ToMinutes(selectedLabelDuration) })}
              </Text>
            )}
          </>
        ) : (
          <>
            {!isMedium && (
              <Text size='medium' weight='bold' color='default'>
                {t('Find Availability')}
              </Text>
            )}
          </>
        )}
        <Icon
          name='caret-down-tiny'
          size={8}
          color='light'
          css={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
        />
      </NakedButton>
      {!useNameLabel && isMedium && <Tooltip {...tooltipProps}>{t('Find Availability')}</Tooltip>}
      <PopoverMenu {...getMenuProps()}>
        {appointmentTypes.map((apptType, index) => {
          const durationMins = apptType.duration ? iso8601ToMinutes(apptType.duration) : null;
          return (
            <PopoverMenuItem
              key={apptType.id}
              {...getItemProps({
                index,
                onClick: () => {
                  if (apptType.id) onSelect(apptType.id);
                },
              })}
            >
              <div css={styles.menuItem}>
                <Text>{apptType.name}</Text>
                {durationMins !== null && (
                  <Text size='small' color='light' css={{ fontStyle: 'italic', textAlign: 'right' }}>
                    {t('{{count}} min', { count: durationMins })}
                  </Text>
                )}
              </div>
            </PopoverMenuItem>
          );
        })}
      </PopoverMenu>
    </>
  );
};

export const appointmentTypeSelectorStyles = {
  trigger: css`
    display: flex;
    align-items: center;
    white-space: nowrap;
    gap: ${theme.spacing(0.5)};
    border-radius: ${theme.borderRadius.small};
    background-color: ${theme.colors.primary5};
    border: 1px solid ${theme.colors.primary40};
  `,
};

const styles = {
  menuItem: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    gap: ${theme.spacing(3)};
  `,
};
